import { createClient } from '@supabase/supabase-js';
import type { NextRequest } from 'next/server';
import { verify } from 'jsonwebtoken';
import { verifyAccessTokenLocally } from '@wtc/supabase';

// Environment configuration
const isDev = process.env.SUPABASE_ENV === 'dev' || process.env.NODE_ENV === 'development';

// Initialize Supabase client for frontend (with anon key)
const supabaseUrl = isDev 
  ? process.env.NEXT_PUBLIC_SUPABASE_URL_DEV || process.env.NEXT_PUBLIC_SUPABASE_URL!
  : process.env.NEXT_PUBLIC_SUPABASE_URL_PROD || process.env.NEXT_PUBLIC_SUPABASE_URL!;

const supabaseAnonKey = isDev
  ? process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_DEV || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
  : process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

// Create Supabase client for frontend authentication (conditional)
export const supabaseAuth = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// Initialize Supabase client for backend (with service role key)
const supabaseServiceKey = isDev
  ? process.env.SUPABASE_SERVICE_ROLE_KEY_DEV || process.env.SUPABASE_SERVICE_ROLE_KEY
  : process.env.SUPABASE_SERVICE_ROLE_KEY_PROD || process.env.SUPABASE_SERVICE_ROLE_KEY;

export const supabaseAdmin = (supabaseUrl && supabaseServiceKey)
  ? createClient(supabaseUrl, supabaseServiceKey)
  : null;

if (!supabaseAdmin) {
  console.warn('[Supabase Auth] Admin client not initialized - missing SUPABASE_SERVICE_ROLE_KEY or URL');
}

// Log which environment we're using
console.log(`[Supabase Auth] Using ${isDev ? 'DEVELOPMENT' : 'PRODUCTION'} environment: ${supabaseUrl}`);

// ============================================================================
// SUPABASE JWT VERIFICATION UTILITIES
// ============================================================================

/**
 * Interface for Supabase JWT payload
 */
interface SupabaseJWTPayload {
  sub: string;
  email: string;
  role: string;
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  user_metadata?: {
    userType?: string;
    [key: string]: any;
  };
  app_metadata?: {
    provider?: string;
    providers?: string[];
    [key: string]: any;
  };
}

/**
 * Verify Supabase JWT token and extract user information
 */
export function verifySupabaseJWT(token: string): SupabaseJWTPayload | null {
  try {
    // For Supabase, we should use the service role key as the JWT secret
    // or use Supabase's built-in verification
    const jwtSecret = process.env.SUPABASE_SERVICE_ROLE_KEY || 
                     process.env.SUPABASE_JWT_SECRET || 
                     process.env.NEXTAUTH_SECRET;
    
    if (!jwtSecret) {
      console.error('JWT secret not configured');
      console.error('Available env vars:', {
        SUPABASE_SERVICE_ROLE_KEY: !!process.env.SUPABASE_SERVICE_ROLE_KEY,
        SUPABASE_JWT_SECRET: !!process.env.SUPABASE_JWT_SECRET,
        NEXTAUTH_SECRET: !!process.env.NEXTAUTH_SECRET
      });
      
return null;
    }

    // Verify and decode the JWT token
    const decoded = verify(token, jwtSecret) as SupabaseJWTPayload;
    
    // Validate the token structure
    if (!decoded.sub || !decoded.email) {
      console.error('Invalid JWT payload structure:', {
        hasSub: !!decoded.sub,
        hasEmail: !!decoded.email,
        payload: decoded
      });
      
return null;
    }

    return decoded;
  } catch (error) {
    console.error('Error verifying Supabase JWT:', error);
    console.error('Token (first 50 chars):', token.substring(0, 50));
    
return null;
  }
}

/**
 * Extract user email from Supabase JWT token in authorization header
 */
export async function extractUserEmailFromSupabaseToken(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }

    const token = authHeader.substring(7); // Remove 'Bearer ' prefix

    // Fast path: verify locally against the project JWKS (no GoTrue round-trip).
    // See the SUPABASE_JWT_LOCAL_VERIFICATION rule.
    const local = await verifyAccessTokenLocally(token);
    if (local.status === 'verified') return local.claims.email;
    if (local.status === 'expired') return null;

    if (!supabaseAdmin) {
      console.warn('[Supabase Auth] extractUserEmailFromSupabaseToken: supabaseAdmin not initialized');
      return null;
    }
    // Network fallback (symmetric/legacy tokens or local-verify miss).
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);

    if (error || !user) {
      console.error('Error verifying token with Supabase:', error);

return null;
    }

    return user.email || null;
  } catch (error) {
    console.error('Error extracting user email from Supabase token:', error);
    
return null;
  }
}

/**
 * Extract user ID from Supabase JWT token in authorization header
 */
export async function extractUserIdFromSupabaseToken(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }

    const token = authHeader.substring(7); // Remove 'Bearer ' prefix

    // Fast path: verify locally against the project JWKS (no GoTrue round-trip).
    // See the SUPABASE_JWT_LOCAL_VERIFICATION rule.
    const local = await verifyAccessTokenLocally(token);
    if (local.status === 'verified') return local.claims.sub;
    if (local.status === 'expired') return null;

    if (!supabaseAdmin) {
      console.warn('[Supabase Auth] extractUserIdFromSupabaseToken: supabaseAdmin not initialized');
      return null;
    }
    // Network fallback (symmetric/legacy tokens or local-verify miss).
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);

    if (error || !user) {
      console.error('Error verifying token with Supabase:', error);

return null;
    }

    return user.id || null;
  } catch (error) {
    console.error('Error extracting user ID from Supabase token:', error);
    
return null;
  }
}

/**
 * Extract user type from Supabase JWT token in authorization header
 */
export async function extractUserTypeFromSupabaseToken(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }

    const token = authHeader.substring(7); // Remove 'Bearer ' prefix
    
    if (!supabaseAdmin) {
      console.warn('[Supabase Auth] extractUserTypeFromSupabaseToken: supabaseAdmin not initialized');
      return null;
    }
    // Use Supabase's built-in verification instead of manual JWT verification
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);
    
    if (error || !user) {
      console.error('Error verifying token with Supabase:', error);
      
return null;
    }
    
    return user.user_metadata?.userType || 'user';
  } catch (error) {
    console.error('Error extracting user type from Supabase token:', error);
    
return null;
  }
}

/**
 * Validate Supabase authorization header and extract user information
 */
export function validateSupabaseAuthHeader(request: NextRequest): {
  isValid: boolean;
  email: string | null;
  userId: string | null;
  userType: string | null;
  token: string | null;
} {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return {
        isValid: false,
        email: null,
        userId: null,
        userType: null,
        token: null,
      };
    }

    const token = authHeader.substring(7);
    const decoded = verifySupabaseJWT(token);

    if (!decoded) {
      return {
        isValid: false,
        email: null,
        userId: null,
        userType: null,
        token: null,
      };
    }

    return {
      isValid: true,
      email: decoded.email,
      userId: decoded.sub,
      userType: decoded.user_metadata?.userType || 'user',
      token,
    };
  } catch (error) {
    console.error('Error validating Supabase auth header:', error);
    
return {
      isValid: false,
      email: null,
      userId: null,
      userType: null,
      token: null,
    };
  }
}

// ============================================================================
// SUPABASE AUTHENTICATION FUNCTIONS
// ============================================================================

/**
 * Sign in with email and password using Supabase
 */
export async function signInWithSupabase(email: string, password: string) {
  try {
    if (!supabaseAuth) {
      return { success: false, error: 'Supabase client not initialized' };
    }
    const { data, error } = await supabaseAuth.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Supabase sign in error:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error signing in with Supabase:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Sign up with email and password using Supabase
 */
export async function signUpWithSupabase(email: string, password: string, userType: string = 'user') {
  try {
    if (!supabaseAuth) {
      return { success: false, error: 'Supabase client not initialized' };
    }
    const { data, error } = await supabaseAuth.auth.signUp({
      email,
      password,
      options: {
        data: {
          userType,
        },
      },
    });

    if (error) {
      console.error('Supabase sign up error:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error signing up with Supabase:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Sign out using Supabase
 */
export async function signOutWithSupabase() {
  try {
    if (!supabaseAuth) {
      return { success: false, error: 'Supabase client not initialized' };
    }
    // Device scope only — never global (see SignOutScope in @wtc/supabase).
    const { error } = await supabaseAuth.auth.signOut({ scope: "local" });

    if (error) {
      console.error('Supabase sign out error:', error);
      
return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error signing out with Supabase:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Get current user session from Supabase
 */
export async function getCurrentSupabaseUser() {
  try {
    if (!supabaseAuth) {
      return null;
    }
    const { data: { user }, error } = await supabaseAuth.auth.getUser();

    if (error) {
      console.error('Error getting current Supabase user:', error);
      
return null;
    }

    return user;
  } catch (error) {
    console.error('Error getting current Supabase user:', error);
    
return null;
  }
}

/**
 * Get current session from Supabase
 */
export async function getCurrentSupabaseSession() {
  try {
    if (!supabaseAuth) {
      return null;
    }
    const { data: { session }, error } = await supabaseAuth.auth.getSession();

    if (error) {
      console.error('Error getting current Supabase session:', error);
      
return null;
    }

    return session;
  } catch (error) {
    console.error('Error getting current Supabase session:', error);
    
return null;
  }
}

/**
 * Update user metadata in Supabase
 */
export async function updateSupabaseUserMetadata(userId: string, metadata: Record<string, any>) {
  try {
    if (!supabaseAdmin) {
      return { success: false, error: 'Supabase admin client not initialized' };
    }
    const { data, error } = await supabaseAdmin.auth.admin.updateUserById(userId, {
      user_metadata: metadata,
    });

    if (error) {
      console.error('Error updating Supabase user metadata:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error updating Supabase user metadata:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Create user in Supabase (admin function)
 */
export async function createSupabaseUser(email: string, password: string, userType: string = 'user') {
  try {
    if (!supabaseAdmin) {
      return { success: false, error: 'Supabase admin client not initialized' };
    }
    const { data, error } = await supabaseAdmin.auth.admin.createUser({
      email,
      password,
      user_metadata: {
        userType,
      },
      email_confirm: true, // Auto-confirm email
    });

    if (error) {
      console.error('Error creating Supabase user:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error creating Supabase user:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Delete user from Supabase (admin function)
 */
export async function deleteSupabaseUser(userId: string) {
  try {
    if (!supabaseAdmin) {
      return { success: false, error: 'Supabase admin client not initialized' };
    }
    const { data, error } = await supabaseAdmin.auth.admin.deleteUser(userId);

    if (error) {
      console.error('Error deleting Supabase user:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error deleting Supabase user:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get client IP address from request
 */
export function getClientIP(request: NextRequest): string | null {
  return request.headers.get('x-forwarded-for')?.split(',')[0] || 
         request.headers.get('x-real-ip') || 
         null;
}

/**
 * Get user agent from request
 */
export function getUserAgent(request: NextRequest): string | null {
  return request.headers.get('user-agent') || null;
}

/**
 * Check if user has admin role
 */
export function isAdminUser(userType: string | null): boolean {
  return userType === 'admin' || userType === 'super_admin';
}

/**
 * Check if user has manager role or higher
 */
export function isManagerOrHigher(userType: string | null): boolean {
  return ['admin', 'super_admin', 'manager'].includes(userType || '');
}

